/**
 * MA-52: de telpunten van de Amsterdamse crowdmonitor (CMSA) als laag onder de
 * woningpunten. Hoe drukker, hoe groter het rondje.
 *
 * De publicatie op data.amsterdam.nl staat stil sinds 28-03-2024; daarom zit de
 * data als vast bestand in de app (`passanten.json`, gemaakt door
 * `apps/web/scripts/passanten.mjs`) en vraagt de kaart niets op bij de gemeente.
 */
import type { CircleLayerSpecification, GeoJSONSourceSpecification } from 'maplibre-gl';
import passanten from './passanten.json';

export const BRON_PASSANTEN = 'passanten-telpunten';
export const LAAG_PASSANTEN = 'passanten-rondjes';

export const PASSANTEN_ATTRIBUTIE =
  '<a href="https://data.amsterdam.nl/">Gemeente Amsterdam</a> (crowdmonitor)';

export type Telpunt = {
  sensor: string;
  naam: string;
  perDag: number;
  dagen: number;
  van: string;
  tot: string;
  corona: boolean;
};

export const PASSANTEN_BRON: GeoJSONSourceSpecification = {
  type: 'geojson',
  data: passanten as GeoJSONSourceSpecification['data'],
  attribution: PASSANTEN_ATTRIBUTIE,
};

const KLEUR = '#8c2f20';

/**
 * De straal loopt met de wortel van het aantal mee, zodat het oppervlak van het
 * rondje de drukte draagt: de Damstraat (~69.000 per dag) is dan geen vijftig
 * keer zo groot als de Bloedstraat. Coronametingen hebben alleen een rand; ze
 * vallen lager uit dan normaal en mogen niet als even zeker ogen.
 */
export const PASSANTEN_LAAG: CircleLayerSpecification = {
  id: LAAG_PASSANTEN,
  type: 'circle',
  source: BRON_PASSANTEN,
  layout: { visibility: 'none' },
  paint: {
    'circle-radius': [
      'interpolate',
      ['linear'],
      ['zoom'],
      11,
      ['*', 0.6, ['interpolate', ['linear'], ['sqrt', ['get', 'perDag']], 25, 4, 265, 16]],
      16,
      ['*', 1.6, ['interpolate', ['linear'], ['sqrt', ['get', 'perDag']], 25, 4, 265, 16]],
    ],
    'circle-color': KLEUR,
    'circle-opacity': ['case', ['get', 'corona'], 0, 0.3],
    'circle-stroke-color': KLEUR,
    'circle-stroke-width': 1.5,
    'circle-stroke-opacity': 0.8,
  },
};

const getal = new Intl.NumberFormat('nl-NL');
const datum = new Intl.DateTimeFormat('nl-NL', { month: 'short', year: 'numeric' });

/** "apr. 2023 – mrt. 2024", uit de ISO-datums van de eerste en laatste getelde dag. */
export function periode(van: string, tot: string): string {
  return `${datum.format(new Date(van))} – ${datum.format(new Date(tot))}`;
}

/** De popup als DOM, met textContent: de namen komen van buiten. */
export function telpuntInhoud(punt: Telpunt): HTMLElement {
  const blok = document.createElement('div');
  blok.className = 'passanten-popup';
  const regels: [string, string][] = [
    ['passanten-naam', punt.naam],
    ['passanten-aantal', `${getal.format(punt.perDag)} passanten per dag`],
    ['passanten-periode', `gemiddeld over ${punt.dagen} dagen, ${periode(punt.van, punt.tot)}`],
  ];
  if (punt.corona) regels.push(['passanten-corona', 'Gemeten in coronatijd, lager dan normaal.']);
  for (const [klasse, tekst] of regels) {
    const regel = document.createElement('p');
    regel.className = klasse;
    regel.textContent = tekst;
    blok.append(regel);
  }
  return blok;
}
